import { SmartSort } from '@iheartjane/dm-sdk';
import type { SmartSortRotatorSnapshot } from '@iheartjane/utils';
import { SmartSortRotator } from '@iheartjane/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { MenuTopRowResponse } from '@jane/bloom/routes/api.menu-top-row/types';
import { deriveBasename } from '@jane/bloom/utils/deriveBasename';
import { logError } from '@jane/bloom/utils/logError';
import type { AlgoliaProduct } from '@jane/search/types';

const DEFAULT_REFRESH_MS = 15_000;

const seedSnapshot = (
  row: SmartSort<AlgoliaProduct> | undefined
): SmartSortRotatorSnapshot<SmartSort<AlgoliaProduct>> => ({
  isError: false,
  isFetching: false,
  row,
});

interface UseMenuTopRowWithServerDataProps {
  /** Whether refetching is enabled */
  enableRefetch?: boolean;
  /** Initial server-side menuTopRow data (serialized JSON string) */
  initialMenuTopRow?: string;
  /** Refresh time in seconds (defaults to 15) */
  refreshTime?: number;
  /** Search filter string for client-side refetching. Required if enableRefetch is true. */
  searchFilter?: string;
  /** Store ID */
  storeId: number;
}

interface UseMenuTopRowWithServerDataResult {
  /** Whether there was an error */
  isError: boolean;
  /** Whether the query is currently fetching */
  isFetching: boolean;
  /** Whether the query was successful */
  isSuccess: boolean;
  /** Pause refetching */
  pauseRefetching: () => void;
  /** The current menu top row instance */
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
  /** Unpause refetching */
  unpauseRefetching: () => void;
}

/**
 * Seeds the top-of-menu row from server-rendered data and rotates it on the
 * client. The rotation loop (timer, current-row hand-off, pause/resume,
 * self-terminate, request cancellation) lives in the framework-agnostic
 * {@link SmartSortRotator} in `@iheartjane/utils`; this hook only wires it to
 * React, derives the exclude list from the current row, and injects Bloom's
 * transport — a fetch to the same-origin `/api/menu-top-row` route, which keeps
 * the DM decisions call server-side (out of reach of CORS and ad blockers).
 */
export const useMenuTopRowWithServerData = ({
  enableRefetch = true,
  initialMenuTopRow,
  refreshTime = 15,
  searchFilter,
  storeId,
}: UseMenuTopRowWithServerDataProps): UseMenuTopRowWithServerDataResult => {
  const { menuUrl, pinnedStore } = useRootLoaderData();
  // basename is applied to useFetcher but not raw fetch
  const basename = deriveBasename(menuUrl);

  const initialRow = useMemoizedRowInstance(initialMenuTopRow);

  const intervalMs = useMemo(() => {
    if (refreshTime) return refreshTime * 1000;
    return (
      pinnedStore?.menu_top_row_refresh_time?.['ecomm'] ?? DEFAULT_REFRESH_MS
    );
  }, [refreshTime, pinnedStore]);

  const [snapshot, setSnapshot] = useState<
    SmartSortRotatorSnapshot<SmartSort<AlgoliaProduct>>
  >(() => seedSnapshot(initialRow));

  // Reset the displayed row to the new seed when the server payload changes
  // (new route/filter), during render rather than in an effect.
  const [seedKey, setSeedKey] = useState(initialMenuTopRow);
  if (seedKey !== initialMenuTopRow) {
    setSeedKey(initialMenuTopRow);
    setSnapshot(seedSnapshot(initialRow));
  }

  // Holds the live rotator so pause/unpause can reach it without re-rendering.
  const rotatorRef = useRef<SmartSortRotator<SmartSort<AlgoliaProduct>> | null>(
    null
  );

  // The rotator is created and destroyed inside the effect (one instance per
  // run) so a remount — including React StrictMode's mount/unmount/mount — gets
  // a fresh, un-terminated instance rather than reusing a destroyed one.
  useEffect(() => {
    if (!enableRefetch || searchFilter === undefined || !initialRow) {
      rotatorRef.current = null;
      return;
    }

    const seed = initialRow;
    const rotator = new SmartSortRotator<SmartSort<AlgoliaProduct>>({
      fetchNextSet: async ({ current, signal }) => {
        const excludeCreativeIds = current?.['creativeIds'] ?? [];
        const params = new URLSearchParams({
          searchFilter,
          storeId: String(storeId),
          ...(excludeCreativeIds.length > 0
            ? { excludeCreativeIds: JSON.stringify(excludeCreativeIds) }
            : {}),
        });

        let response: Response;
        try {
          response = await fetch(
            `${basename}/api/menu-top-row?${params.toString()}`,
            { signal }
          );
        } catch (error) {
          if (signal.aborted) throw error;
          // Transport-level failure (e.g. network blip): keep showing the
          // current row and retry next cycle rather than flagging an error.
          logError(error, { hook: 'useMenuTopRowWithServerData' });
          return current ?? seed;
        }

        if (!response.ok) {
          const body = await response.text();
          const message = body || `HTTP ${response.status}`;
          logError(new Error(message), {
            hook: 'useMenuTopRowWithServerData',
            status: response.status,
          });
          throw new Error(message);
        }

        const json = (await response.json()) as MenuTopRowResponse;
        if ('error' in json) {
          throw new Error(json.error);
        }
        return SmartSort.fromJSON<AlgoliaProduct>(json.menuTopRow);
      },
      hasItems: (row) => row.products.length > 0,
      intervalMs,
      row: seed,
      stopOnEmpty: true,
    });

    rotatorRef.current = rotator;
    const unsubscribe = rotator.subscribe(() =>
      setSnapshot(rotator.getSnapshot())
    );
    rotator.start();

    return () => {
      unsubscribe();
      rotator.destroy();
      rotatorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableRefetch,
    initialMenuTopRow,
    searchFilter,
    storeId,
    intervalMs,
    basename,
  ]);

  const pauseRefetching = useCallback(() => rotatorRef.current?.pause(), []);
  const unpauseRefetching = useCallback(() => rotatorRef.current?.resume(), []);

  return {
    isError: snapshot.isError,
    isFetching: snapshot.isFetching,
    isSuccess: !!snapshot.row && !snapshot.isError && !snapshot.isFetching,
    pauseRefetching,
    rowInstance: snapshot.row,
    unpauseRefetching,
  };
};
